import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@jsverse/transloco';
import { ModalService } from 'projects/shared/modal/modal.service';
import { v4 as uuidv4 } from 'uuid';
import { SimpleIncomeDialogComponent } from '../wizard-steps/simple-income-dialog/simple-income-dialog.component';
import { IncomeItem, IncomeSubItem } from '../../models/income-item-model';
import { Employment } from '../../models/employment-model';
import { Income } from '../../models/income-model';
import { EmploymentDialogComponent } from '../wizard-steps/employment-dialog/employment-dialog.component';
import { Borrower } from '../../models/borrower-model';
import * as _ from 'lodash';
import { TypeOfIncome } from '../../models/enums/type-of-income.enum';
import { EnumsService } from '../../services/enums.service';
import { EnumerationItem } from 'projects/shared/models/enumeration-item.model';
import { EmploymentTypeEnum } from '../../models/mortgage.model';
import { FieldConfigUtils } from '../../models/wizard/config/field-config-utils';
import { EmploymentIncomeDialogComponent } from '../wizard-steps/employment-income-dialog/employment-income-dialog.component';
import { Constants } from '../../services/constants';

@Component({
    selector: 'borrower-income',
    templateUrl: 'borrower-income.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BorrowerIncomeComponent implements OnInit, AfterViewInit, OnDestroy {

  private modalOptions: NgbModalOptions = {
    size: 'lg',
    backdrop: 'static',
    container: '#income-main',
  };

  @Input() borrower!: Borrower;

  /**
   * Full list of borrowers who are applying — used to populate the borrower
   * picker in the employment edit dialog so users can reassign an employment
   * to a different borrower without deleting and re-creating.
   */
  @Input() borrowers: Borrower[] = [];

  @Input()
  fieldConfig: any;

  @Output()
  incomesChanged: EventEmitter<any> = new EventEmitter<any>();

  incomes: IncomeItem[] = [];

  private _langSubscription: Subscription | undefined;

  private _employmentBasedIncomeTypes: EnumerationItem[] = [];

  private _employmentIncomeTypeValues: string[] = [];

  private _incomeTypes: EnumerationItem[] = [];

  get totalIncome(): number {
    let sum: number = 0;
    const currentIncomes = this.incomes.filter((income) => income.isCurrent);
    currentIncomes.forEach((income) => {
      sum = sum + Number(income.monthlyPay);
    });
    return sum;
  }

  isValid(): boolean {
    let result: boolean = true;
    this.incomes.forEach((income) => {
      if (!income.isValid) {
        result = false;
      }
    });
    return result;
  }

  private _nonEmploymentBasedIncomeTypes: TypeOfIncome[] = [];

  constructor(
    private readonly _modalService: ModalService,
    private readonly _enumsService: EnumsService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _transloco: TranslocoService,
  ) {}

  ngOnInit() {
    this._enumsService.getMortgageEnumerations().subscribe(enums => {
      this._incomeTypes = enums[Constants.enumerations.incomeType];
      this.generateEmploymentIncomeTypeValuesList();
      this.prepareEmploymentIncomeTypesList();
      this._nonEmploymentBasedIncomeTypes = this._incomeTypes
        .filter(it => !this._employmentBasedIncomeTypes.includes(it.value)).map(it => it.value);
      this.populateIncomeItems(this.borrower);
      this._cdr.markForCheck();
    });
    // Saved income cards show translated income-type labels, so rebuild them on an EN/ES switch — once the
    // new language's dictionary has loaded (selectTranslation emits then; langChanges$ fires before it).
    this._langSubscription = this._transloco.selectTranslation().subscribe(() => {
      if (this._incomeTypes && this.borrower) {
        this.populateIncomeItems(this.borrower);
        this._cdr.markForCheck();
      }
    });
  }

  ngAfterViewInit() { }

  ngOnDestroy() {
    this._langSubscription?.unsubscribe();
  }

  refresh = () => {
    this.populateIncomeItems(this.borrower);
    this._cdr.markForCheck();
  };

  onIncomeDeleted = (income: IncomeItem) => {
    const typeOfIncome = this._employmentBasedIncomeTypes.find(t => t.value === income.type);
    if (typeOfIncome && !!income.employmentGuid) {
      this.deleteIncomeFromEmployment(income);
    } else {
      this.deleteIncomeFromNonEmploymentIncomes(income);
    }
    // Either branch changes the borrower's income mix, which can flip the
    // qualifying-income-bypass flag — notify the parent so it recomputes
    // `BorrowerEmploymentInfo.hasQualifiedIncomeForBypass` and the duration
    // indicator updates.
    this.incomesChanged.emit();
    this._cdr.markForCheck();
  };

  onEditIncomeItemsClicked = (income: IncomeItem) => {
    const employment = this.borrower.employments.find(e => e.modelGuid === income.employmentGuid);
    this.showEmploymentIncomeItemsDialog(employment);
  }

  onIncomeEditClicked = (income: IncomeItem) => {
    const incomeItemToEdit = this.incomes.find((i) => i.guid === income.guid);
    if (!incomeItemToEdit) {
      return;
    }

    if (this._employmentBasedIncomeTypes.map(t => t.value).includes(incomeItemToEdit.type)) {
      this.handleEmploymentIncomeClick(incomeItemToEdit);
    } else if (this._nonEmploymentBasedIncomeTypes.includes(incomeItemToEdit.type)) {
      this.handleNonEmploymentIncomeClick(incomeItemToEdit);
    }
  };

  private handleEmploymentIncomeClick = (incomeItemToEdit: IncomeItem) => {
    const employmentToEdit = this.borrower.employments.find(
      (e) => e.modelGuid === incomeItemToEdit.employmentGuid
    );
    if (employmentToEdit) {
      if (employmentToEdit.incomes && employmentToEdit.incomes.length) {
        employmentToEdit.businessType = employmentToEdit.incomes[0].businessType;
        const baseIncome = employmentToEdit.incomes.find(i => i.typeOfIncome === TypeOfIncome.Base
          || i.typeOfIncome === TypeOfIncome.MilitaryCombatPay
        );
        if (baseIncome) {
          employmentToEdit.monthlyIncome = baseIncome.monthlyIncome;
        }
      }
      this.showEmploymentIncomeDialog(incomeItemToEdit.type, _.cloneDeep(employmentToEdit)
      );
    }
  }

  private handleNonEmploymentIncomeClick = (incomeItemToEdit: IncomeItem) => {
    const incomeToEdit = this.borrower.nonEmploymentIncomes.find(
      (e) => e.modelGuid === incomeItemToEdit.guid
    );
    if (incomeToEdit) {
      const header = this.getHeaderForNonEmploymentIncome(incomeToEdit.typeOfIncome);
      this.showNonEmploymentIncomeDialog(header, this._transloco.translate('wizard.income.monthlyPay'), incomeItemToEdit.type, incomeToEdit);
    }
  }

  private deleteIncomeFromEmployment(income: IncomeItem) {
    const index = this.incomes.indexOf(income);
    const employments = this.borrower.employments;
    if (index >= 0) {
      this.incomes.splice(index, 1);
      const employmentIndexToDelete = employments.findIndex(
        (e) => e.modelGuid === income.employmentGuid
      );
      if (employmentIndexToDelete >= 0) {
        employments.splice(index, 1);
      }
    }
  }

  private deleteIncomeFromNonEmploymentIncomes(income: IncomeItem) {
    const index = this.incomes.indexOf(income);
    const nonEmploymentIncomes = this.borrower.nonEmploymentIncomes;
    if (index >= 0) {
      this.incomes.splice(index, 1);
      const incomeToDelete = nonEmploymentIncomes.find(
        (i) => i.modelGuid === income.guid
      );
      if (incomeToDelete) {
        const index = nonEmploymentIncomes.indexOf(incomeToDelete);
        if (index >= 0) {
          nonEmploymentIncomes.splice(index, 1);
        }
      }
    }
  }

  private isEmploymentValid(employment: Employment): boolean {
    if (FieldConfigUtils.isRequied(this.fieldConfig, 'employment.employer') &&
      (!employment.employer || employment.employer.length == 0)) {
      return false;
    }

    if (FieldConfigUtils.isRequied(this.fieldConfig, 'employment.employmentType') && (employment.employmentType == null)) {
      return false;
    }

    if (FieldConfigUtils.isRequied(this.fieldConfig, 'employment.startDate') && !employment.startDate) {
      return false;
    }

    if(!(employment.employer && employment.employer == "Retired" && employment.employmentType == EmploymentTypeEnum.CurrentEmployer)){

      if (FieldConfigUtils.isRequied(this.fieldConfig, 'employment.position') && (!employment.position || employment.position.length == 0)) {
        return false;
      }
      if (employment.isPrimary == null) {
        return false;
      }

      if (!employment.selfEmployed) {
        if (employment.incomes == null || employment.incomes.length == 0) {
          return false;
        }
      } else {
        if (!employment.selfEmploymentMonthlyIncomeOrLoss) {
          return false;
        }
      }
      if (FieldConfigUtils.isRequied(this.fieldConfig, 'employment.yearsInLineOfWork') &&
        (employment.yearsInLineOfWork == null ||
        employment.yearsInLineOfWork <= 0)
      ) {
        return false;
      }
    }
    return true;
  }

  private isIncomeValid(income: Income): boolean {
    if (income.typeOfIncome == TypeOfIncome.OtherTypesOfIncome) {
      if (
        !income.typeOfIncomeOtherDescription ||
        income.typeOfIncomeOtherDescription.length == 0
      ) {
        return false;
      }
    }
    if ((!income.monthlyIncome || income.monthlyIncome <= 0)) {
      return false;
    }
    return true;
  }

  private toEmploymentIncomeItem = (
    employment: Employment
  ): IncomeItem => {

    const incomeItem = new IncomeItem(
      TypeOfIncome.Base,
      0,
      this.isEmploymentValid(employment),
      employment.position,
      employment.startDate!,
      employment.endDate!
    );
    incomeItem.employmentGuid = employment.modelGuid;
    incomeItem.isSelfEmployed = employment.selfEmployed || false;
    incomeItem.isCurrent = employment.employmentType === EmploymentTypeEnum.CurrentEmployer;
    if (employment.incomes) {
      if (employment.incomes.length === 1) {
        incomeItem.employmentGuid = employment.modelGuid;
        incomeItem.guid = employment.incomes[0].modelGuid;
        incomeItem.type = employment.incomes[0].typeOfIncome;
      }
      employment.incomes.forEach((income) => {
        if (income) {
          const subIncomeItem = new IncomeSubItem();
          subIncomeItem.type = income.typeOfIncome!;
          subIncomeItem.monthlyPay = income.monthlyIncome!;
          subIncomeItem.guid = income.modelGuid;
          incomeItem.incomeBreakdown.push(subIncomeItem);
        }
      });
    }

    incomeItem.name = employment.employer;
    incomeItem.employmentGuid = employment.modelGuid;
    if (!incomeItem.isSelfEmployed) {
      incomeItem.monthlyPay = incomeItem.incomeBreakdown.reduce((total, subIncome) => total + Number(subIncome.monthlyPay), 0);
    } else {
      incomeItem.monthlyPay = employment.selfEmploymentMonthlyIncomeOrLoss;
    }
    return incomeItem;
  };

  private toIncomeItemNonEmployment = (income: Income): IncomeItem => {
    const incomeItem = new IncomeItem(
      income.typeOfIncome!,
      income.monthlyIncome!,
      this.isIncomeValid(income)
    );
    incomeItem.name = this.typeOfIncomeDisplayText(income.typeOfIncome!);
    incomeItem.id = income.incomeId;
    if (income.typeOfIncome == TypeOfIncome.OtherTypesOfIncome) {
      incomeItem.position = income.typeOfIncomeOtherDescription;
    }
    incomeItem.guid = income.modelGuid;
    incomeItem.employmentGuid = undefined;
    return incomeItem;
  };

  private typeOfIncomeDisplayText = (typeOfIncome: TypeOfIncome): string => {
    // Same URLA label the Other income picker shows (enums.incomeType.{value}) in the active language;
    // the server's English enum description is only a fallback for untranslated values.
    const translations = this._transloco.getTranslation(this._transloco.getActiveLang()) as Record<string, string> | undefined;
    const translated = translations?.[`enums.incomeType.${typeOfIncome}`];
    if (translated) {
      return translated;
    }
    const incomeType = this._incomeTypes.find(it => it.value === typeOfIncome);
    if (incomeType) {
      return incomeType.name;
    }
    return typeOfIncome;
  };

  private populateIncomeItems(borrower: Borrower) {
    this.incomes = [];
    borrower.employments.forEach((employment) => {
      const employmentIncomeItem = this.toEmploymentIncomeItem(employment);
      this.incomes.push(employmentIncomeItem);
    });

    borrower.nonEmploymentIncomes.forEach((nonEmploymentIncome) =>
      this.incomes.push(this.toIncomeItemNonEmployment(nonEmploymentIncome))
    );
  }

  private showEmploymentIncomeItemsDialog = (employment: Employment) => {
    const modal = this._modalService.show(
      EmploymentIncomeDialogComponent,
      this.modalOptions
    );
    modal.componentInstance.employment = employment;
    modal.result.then(
      (updatedEmployment) => {
        const updatedEmploymentIncomeItem = this.toEmploymentIncomeItem(updatedEmployment);
        const indexOfEmployment = this.borrower.employments.findIndex(e => e.modelGuid === updatedEmployment.modelGuid);
        if (indexOfEmployment >= 0) {
          this.borrower.employments[indexOfEmployment] = updatedEmployment;
        }
        const index = this.incomes.findIndex((i) => i.employmentGuid === updatedEmployment.modelGuid);
        if (index >= 0) {
          this.incomes[index] = updatedEmploymentIncomeItem;
        }
        this._cdr.markForCheck();
      },
      (error) => { }
    );
  }

  private showEmploymentIncomeDialog(
    typeOfIncome: TypeOfIncome,
    employment?: Employment
  ) {
    const modal = this._modalService.show(
      EmploymentDialogComponent,
      this.modalOptions
    );
    modal.componentInstance.typeOfIncome = typeOfIncome;
    modal.componentInstance.fieldsToConfig = this.fieldConfig;
    modal.componentInstance.borrowers = this.borrowers?.length ? this.borrowers : [this.borrower];
    if (employment) {
      modal.componentInstance.employment = employment;
    } else if (
      typeOfIncome == TypeOfIncome.ContractBasis ||
      typeOfIncome === TypeOfIncome.SelfEmploymentIncome
    ) {
      modal.componentInstance.employment.selfEmployed = true;
    }
    modal.result.then(
      (employmentAndIncomeType) => {
        this.onSaveClickedOnEmploymentModalForEdit(employmentAndIncomeType.employment, typeOfIncome);
        this._cdr.markForCheck();
      },
      (error) => { }
    );
  }

  private onSaveClickedOnIncomeModalForEdit = (
    income: Income,
    typeOfIncome: TypeOfIncome
  ): void => {
    // If the user reassigned this income to a different borrower via the
    // picker, move it across. Parent (income-step) refreshes all borrower
    // sections via `incomesChanged` so both cards re-render.
    const targetBorrowerId = income.borrowerId;
    if (targetBorrowerId != null && targetBorrowerId !== this.borrower.borrowerId) {
      const newBorrower = this.borrowers?.find(b => b.borrowerId === targetBorrowerId);
      if (newBorrower) {
        const oldIndex = this.borrower.nonEmploymentIncomes?.findIndex(
          i => i.modelGuid === income.modelGuid
        ) ?? -1;
        if (oldIndex >= 0) {
          this.borrower.nonEmploymentIncomes.splice(oldIndex, 1);
        }
        // Server keys the record by incomeId/modelGuid under the original
        // borrower. Reset IDs so the server treats this as a fresh insert
        // under the new borrower rather than deleting + rejecting.
        income.incomeId = 0;
        income.modelGuid = uuidv4();
        if (!newBorrower.nonEmploymentIncomes) {
          newBorrower.nonEmploymentIncomes = [];
        }
        newBorrower.nonEmploymentIncomes.push(income);
        this.refresh();
        this.incomesChanged.emit();
        return;
      }
    }

    const nonEmploymentIncomes = this.borrower.nonEmploymentIncomes;
    if (!nonEmploymentIncomes) {
      return;
    }
    const existingNonEmploymentIncome = nonEmploymentIncomes?.find(
      (e) => e.modelGuid === income.modelGuid
    );
    if (!existingNonEmploymentIncome) {
      return;
    }
    const index = nonEmploymentIncomes?.indexOf(existingNonEmploymentIncome);
    nonEmploymentIncomes[index] = income;

    const incomeItem = this.incomes.find((i) => i.guid === income.modelGuid);
    if (incomeItem) {
      const index = this.incomes.indexOf(incomeItem);
      const updatedIncomeItem = this.toIncomeItemNonEmployment(income);
      this.incomes[index] = updatedIncomeItem;
    }
    // Editing the income type can change whether the borrower qualifies for
    // the 2-year employment-history bypass (e.g. switching "Other" → "Pension").
    this.incomesChanged.emit();
  };

  private onSaveClickedOnEmploymentModalForEdit = (
    employment: Employment,
    typeOfIncome: TypeOfIncome
  ): void => {
    // Always rewrite the base income on the employment first so downstream
    // consumers (including the case where we move the employment to a
    // different borrower) see the updated values.
    const monthlyIncome = (typeOfIncome === TypeOfIncome.SelfEmploymentIncome)
      ? employment.selfEmploymentMonthlyIncomeOrLoss : employment.monthlyIncome;
    let baseIncome = employment.incomes.find(i => i.typeOfIncome === TypeOfIncome.Base ||
      i.typeOfIncome === TypeOfIncome.MilitaryCombatPay
    );
    if (!baseIncome) {
      baseIncome = new Income();
      employment.incomes.push(baseIncome);
    }
    baseIncome.monthlyIncome = Number(monthlyIncome);
    baseIncome.businessType = employment.businessType;
    baseIncome.typeOfIncome = typeOfIncome;

    // If the user reassigned the employment to a different borrower via the
    // picker, move it across. Parent (income-step) refreshes all borrower
    // sections via the `incomesChanged` event so both the old and new
    // cards re-render correctly.
    const targetBorrowerId = employment.borrowerId;
    if (targetBorrowerId != null && targetBorrowerId !== this.borrower.borrowerId) {
      const newBorrower = this.borrowers?.find(b => b.borrowerId === targetBorrowerId);
      if (newBorrower) {
        const oldIndex = this.borrower.employments.findIndex(
          e => e.modelGuid === employment.modelGuid
        );
        if (oldIndex >= 0) {
          this.borrower.employments.splice(oldIndex, 1);
        }
        // Server keys the record by employmentId/modelGuid under the original
        // borrower. A cross-borrower move would make the server delete the
        // record from the old borrower AND reject/ignore it under the new one
        // (id mismatch). Reset IDs so the server treats this as a fresh insert
        // under the new borrower.
        employment.employmentId = 0;
        employment.modelGuid = uuidv4();
        employment.incomes?.forEach(i => {
          i.incomeId = 0;
          i.modelGuid = uuidv4();
        });
        if (!newBorrower.employments) {
          newBorrower.employments = [];
        }
        newBorrower.employments.push(employment);
        this.refresh();
        this.incomesChanged.emit();
        return;
      }
    }

    const employments = this.borrower.employments;
    if (employments) {
      const existingEmploymentIndex = employments?.findIndex(
        (e) => e.modelGuid === employment.modelGuid
      );
      if (existingEmploymentIndex >= 0) {
        employments[existingEmploymentIndex] = employment;

        const incomeItem = this.incomes.find(
          (i) => i.employmentGuid === employment.modelGuid
        );
        if (incomeItem) {
          const index = this.incomes.indexOf(incomeItem);
          const updatedIncomeItem = this.toEmploymentIncomeItem(
            employment
          );
          this.incomes[index] = updatedIncomeItem;
        }
        this.incomesChanged.emit();
      }
    }
  };

  private showNonEmploymentIncomeDialog = (
    header: string,
    label: string,
    typeOfIncome: TypeOfIncome,
    income?: Income
  ) => {
    const modal = this._modalService.show(
      SimpleIncomeDialogComponent,
      this.modalOptions
    );
    modal.componentInstance.header = header;
    modal.componentInstance.label = label;
    modal.componentInstance.borrowers = this.borrowers?.length ? this.borrowers : [this.borrower];
    if (income) {
      modal.componentInstance.income = income;
    } else {
      modal.componentInstance.typeOfIncome = typeOfIncome;
    }
    modal.result.then(
      (income) => {
        this.onSaveClickedOnIncomeModalForEdit(income, typeOfIncome);
        this._cdr.markForCheck();
      },
      (error) => { }
    );
  };

  private getHeaderForNonEmploymentIncome = (typeOfIncome: TypeOfIncome): string => {
    // Prefer the localized enum label via Transloco key `enums.incomeType.{value}`;
    // fall back to the server-provided English name, then to a generic 'Other'.
    const key = `enums.incomeType.${typeOfIncome}`;
    const translations = this._transloco.getTranslation(this._transloco.getActiveLang()) as Record<string, string> | undefined;
    const translated = translations?.[key];
    if (translated) {
      return translated;
    }
    const type = this._incomeTypes.find(it => it.value === typeOfIncome);
    if (type) {
      return type.name;
    }
    return 'Other';
  }

  private generateEmploymentIncomeTypeValuesList = () => {
    this._employmentIncomeTypeValues = [
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.Base),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.Bonus),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.Commissions),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.Overtime),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.MilitaryCombatPay),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.MilitaryFlightPay),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.MilitaryHazardPay),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.MilitaryOverseasPay),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.MilitaryPropPay),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.MilitaryClothesAllowance),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.MilitaryRationsAllowance),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.MilitaryVariableHousingAllowance),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.MilitaryQuartersAllowance),
      this._enumsService.getEnumValue(Constants.enumerationValueNames.IncomeType.ContractBasis),
    ]
  }

  private prepareEmploymentIncomeTypesList = () => {
    const employmentIncomeTypes = [];
    this._employmentIncomeTypeValues.forEach(type => {
      const incomeType = this._incomeTypes.find(t => t.value === type);
      if (incomeType) {
        employmentIncomeTypes.push(incomeType);
      }
    });
    this._employmentBasedIncomeTypes = employmentIncomeTypes;
  }
}
